import React, { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/ui/dialog'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Label } from '~/ui/label'
import { Textarea } from '~/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Checkbox } from '~/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '~/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/ui/collapsible'
import { Badge } from '~/ui/badge'
import { ScrollArea } from '~/ui/scroll-area'
import { X, ChevronDown, ChevronRight, ImageIcon, VolumeX, Loader2, RefreshCw } from 'lucide-react'
import { useToast } from '~/hooks/use-toast'
import { directImageUpload } from '~/common/direct-image-upload'
import { useSafeMutation } from '~/common/use-safe-mutation'

const CREATE_TRACK_MUTATION = gql(`
  mutation CreateTrack($input: CreateTrackInput!) {
    createTrack(input: $input) {
      id
      name
      licensingPro
      licensingProCheckedAt
    }
  }
`)

const UPDATE_TRACK_MUTATION = gql(`
  mutation UpdateTrack($input: UpdateTrackInput!) {
    updateTrack(input: $input) {
      id
      name
      licensingPro
      licensingProCheckedAt
    }
  }
`)

const RECHECK_TRACK_LICENSING_PRO_MUTATION = gql(`
  mutation RecheckTrackLicensingPro($input: RecheckTrackLicensingProInput!) {
    recheckTrackLicensingPro(input: $input) {
      id
      name
      licensingPro
      licensingProCheckedAt
    }
  }
`)

const ARTISTS_QUERY = gql(`
  query ArtistsForTrackModal {
    artists(first: 200) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

const ALBUMS_QUERY = gql(`
  query AlbumsForTrackModal {
    albums(first: 200) {
      edges {
        node {
          id
          name
          releaseDate
        }
      }
    }
  }
`)

interface TrackModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  track?: any
}

interface TrackFormData {
  name: string
  durationMs: string
  trackNumber: string
  discNumber: string
  explicit: boolean
  popularity: string
  albumId: string
  artistIds: string[]
  cover: File | null
  audio: File | null
  audioFeatures: {
    acousticness: string
    bpm: string
    danceability: string
    energy: string
    instrumentalness: string
    key: string
    liveness: string
    loudness: string
    lyrics: string
    mode: string
    speechiness: string
    tempo: string
    timeSignature: string
    valence: string
  }
}

const initialFormData: TrackFormData = {
  name: '',
  durationMs: '',
  trackNumber: '',
  discNumber: '1',
  explicit: false,
  popularity: '0',
  albumId: 'none',
  artistIds: [],
  cover: null,
  audio: null,
  audioFeatures: {
    acousticness: '',
    bpm: '',
    danceability: '',
    energy: '',
    instrumentalness: '',
    key: '',
    liveness: '',
    loudness: '',
    lyrics: '',
    mode: '',
    speechiness: '',
    tempo: '',
    timeSignature: '',
    valence: '',
  },
}

export function TrackModal({ isOpen, onClose, onSaved, track }: TrackModalProps) {
  const { toast } = useToast()
  const [formData, setFormData] = useState<TrackFormData>(initialFormData)
  const [isAudioFeaturesOpen, setIsAudioFeaturesOpen] = useState(false)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [audioPreview, setAudioPreview] = useState<string | null>(null)
  const [licensingInfo, setLicensingInfo] = useState<{
    pro: string | null
    checkedAt: string | null
  }>({
    pro: null,
    checkedAt: null,
  })

  const isEditing = !!track

  // Queries for dropdown options
  const { data: artistsData } = useQuery(ARTISTS_QUERY)
  const { data: albumsData } = useQuery(ALBUMS_QUERY)

  // Mutations
  const [createTrack, { loading: creating }] = useSafeMutation(CREATE_TRACK_MUTATION)
  const [updateTrack, { loading: updating }] = useSafeMutation(UPDATE_TRACK_MUTATION)
  const [recheckTrackPro, { loading: recheckingLicensing }] = useSafeMutation(
    RECHECK_TRACK_LICENSING_PRO_MUTATION
  )

  const loading = creating || updating

  // Populate form when editing
  useEffect(() => {
    if (track && isOpen) {
      setFormData({
        name: track.name || '',
        durationMs: track.durationMs?.toString() || '',
        trackNumber: track.trackNumber?.toString() || '',
        discNumber: track.discNumber?.toString() || '1',
        explicit: track.explicit || false,
        popularity: track.popularity?.toString() || '0',
        albumId: track.album?.id || '',
        artistIds: track.artists?.map((a: any) => a.id) || [],
        cover: null,
        audio: null,
        audioFeatures: {
          acousticness: track.trackAudioFeature?.acousticness?.toString() || '',
          bpm: track.trackAudioFeature?.bpm?.toString() || '',
          danceability: track.trackAudioFeature?.danceability?.toString() || '',
          energy: track.trackAudioFeature?.energy?.toString() || '',
          instrumentalness: track.trackAudioFeature?.instrumentalness?.toString() || '',
          key: track.trackAudioFeature?.key?.toString() || '',
          liveness: track.trackAudioFeature?.liveness?.toString() || '',
          loudness: track.trackAudioFeature?.loudness?.toString() || '',
          lyrics: track.trackAudioFeature?.lyrics || '',
          mode: track.trackAudioFeature?.mode?.toString() || '',
          speechiness: track.trackAudioFeature?.speechiness?.toString() || '',
          tempo: track.trackAudioFeature?.tempo?.toString() || '',
          timeSignature: track.trackAudioFeature?.timeSignature?.toString() || '',
          valence: track.trackAudioFeature?.valence?.toString() || '',
        },
      })
      setCoverPreview(track.coverThumbUrl || null)
      setAudioPreview(track.audioUrl || null)
      setLicensingInfo({
        pro: track.licensingPro ?? null,
        checkedAt: track.licensingProCheckedAt ?? null,
      })
    } else if (!isOpen) {
      setFormData(initialFormData)
      setCoverPreview(null)
      setAudioPreview(null)
      setIsAudioFeaturesOpen(false)
      setLicensingInfo({ pro: null, checkedAt: null })
    }
  }, [track, isOpen])

  const formatLicensingTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Not yet checked'

    const parsed = new Date(timestamp)
    if (Number.isNaN(parsed.getTime())) {
      return 'Not yet checked'
    }

    return parsed.toLocaleString()
  }

  const handleRecheckLicensing = async () => {
    if (!track) return

    try {
      const result = await recheckTrackPro({
        variables: {
          input: {
            id: track.id,
          },
        },
      })

      const updated = result.data?.recheckTrackLicensingPro
      if (updated) {
        setLicensingInfo({
          pro: updated.licensingPro ?? null,
          checkedAt: updated.licensingProCheckedAt ?? null,
        })

        toast({
          title: 'Licensing updated',
          description: updated.licensingPro
            ? `Primary PRO set to ${updated.licensingPro}`
            : 'No performing rights organization match found.',
        })

        // Ensure the parent list/view refreshes so the modal (and any table rows)
        // reflect the updated `licensingProCheckedAt` from the server.
        onSaved()
        return
      }

      // If Apollo was configured to not throw (see useSafeMutation), GraphQL errors will not hit catch().
      // Show a helpful message instead of silently doing nothing.
      const graphQLErrorMessage =
        result.errors?.[0]?.message ||
        // @ts-expect-error apollo result may include `error` depending on link chain
        result.error?.message

      toast({
        title: 'Licensing lookup failed',
        description: graphQLErrorMessage || "We couldn't refresh licensing information. Please try again later.",
        variant: 'destructive',
      })
    } catch (error) {
      console.error('Failed to refresh licensing information', error)
      toast({
        title: 'Licensing lookup failed',
        description: "We couldn't refresh licensing information. Please try again later.",
        variant: 'destructive',
      })
    }
  }

  const handleInputChange = (field: keyof TrackFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleAudioFeatureChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      audioFeatures: {
        ...prev.audioFeatures,
        [field]: value,
      },
    }))
  }

  const handleFileChange = (field: 'cover' | 'audio', file: File | null) => {
    setFormData((prev) => ({ ...prev, [field]: file }))

    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (field === 'cover') {
          setCoverPreview(e.target?.result as string)
        } else {
          setAudioPreview(e.target?.result as string)
        }
      }
      if (field === 'cover') {
        reader.readAsDataURL(file)
      } else {
        setAudioPreview(file.name) // Just show filename for audio
      }
    } else {
      if (field === 'cover') {
        setCoverPreview(track?.coverThumbUrl || null)
      } else {
        setAudioPreview(track?.audioUrl || null)
      }
    }
  }

  const handleArtistToggle = (artistId: string) => {
    setFormData((prev) => ({
      ...prev,
      artistIds: prev.artistIds.includes(artistId)
        ? prev.artistIds.filter((id) => id !== artistId)
        : [...prev.artistIds, artistId],
    }))
  }

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Track name is required',
        variant: 'destructive',
      })
      return false
    }
    return true
  }

  const prepareAudioFeatures = () => {
    const features: any = {}
    Object.entries(formData.audioFeatures).forEach(([key, value]) => {
      if (value.trim()) {
        if (key === 'lyrics') {
          features[key] = value
        } else if (key === 'key' || key === 'mode') {
          features[key] = parseInt(value)
        } else {
          features[key] = parseFloat(value)
        }
      }
    })
    return Object.keys(features).length > 0 ? features : null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      // Upload files and get signed IDs
      let coverSignedId = null
      let audioSignedId = null

      if (formData.cover) {
        const coverUpload = await directImageUpload(formData.cover)
        coverSignedId = coverUpload.signedId
      }

      if (formData.audio) {
        const audioUpload = await directImageUpload(formData.audio)
        audioSignedId = audioUpload.signedId
      }

      const input: any = {
        name: formData.name,
        durationMs: formData.durationMs ? parseInt(formData.durationMs) : null,
        trackNumber: formData.trackNumber ? parseInt(formData.trackNumber) : null,
        discNumber: parseInt(formData.discNumber),
        explicit: formData.explicit,
        popularity: parseInt(formData.popularity),
        albumId: formData.albumId && formData.albumId !== 'none' ? formData.albumId : null,
        artistIds: formData.artistIds.length > 0 ? formData.artistIds : null,
        coverSignedId: coverSignedId,
        audioSignedId: audioSignedId,
        audioFeatures: prepareAudioFeatures(),
      }

      if (isEditing) {
        input.id = track.id
        await updateTrack({ variables: { input } })
        toast({
          title: 'Success',
          description: 'Track updated successfully',
        })
      } else {
        await createTrack({ variables: { input } })
        toast({
          title: 'Success',
          description: 'Track created successfully',
        })
      }

      onSaved()
    } catch (error) {
      console.error('Error saving track:', error)
      toast({
        title: 'Error',
        description: 'Failed to save track. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const artists = artistsData?.artists.edges.map((edge: any) => edge.node) || []
  const albums = albumsData?.albums.edges.map((edge: any) => edge.node) || []

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col space-y-6">
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Track' : 'Add New Track'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update track information and metadata'
                : 'Add a new track to your music library'}
            </DialogDescription>
          </DialogHeader>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Track Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter track name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="trackNumber">Track Number</Label>
                  <Input
                    id="trackNumber"
                    type="number"
                    value={formData.trackNumber}
                    onChange={(e) => handleInputChange('trackNumber', e.target.value)}
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div>
                  <Label htmlFor="discNumber">Disc Number</Label>
                  <Input
                    id="discNumber"
                    type="number"
                    value={formData.discNumber}
                    onChange={(e) => handleInputChange('discNumber', e.target.value)}
                    placeholder="1"
                    min="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (ms)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.durationMs}
                    onChange={(e) => handleInputChange('durationMs', e.target.value)}
                    placeholder="180000"
                    min="0"
                  />
                </div>
                <div>
                  <Label htmlFor="popularity">Popularity (0-100)</Label>
                  <Input
                    id="popularity"
                    type="number"
                    value={formData.popularity}
                    onChange={(e) => handleInputChange('popularity', e.target.value)}
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="explicit"
                  checked={formData.explicit}
                  onCheckedChange={(checked) => handleInputChange('explicit', checked)}
                />
                <Label htmlFor="explicit">Explicit Content</Label>
              </div>
            </CardContent>
          </Card>

          {/* Licensing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Licensing &amp; PRO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isEditing ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">Current PRO:</span>
                      {licensingInfo.pro ? (
                        <Badge variant="outline" className="tracking-wide">
                          {licensingInfo.pro}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Not Matched</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Last checked: {formatLicensingTimestamp(licensingInfo.checkedAt)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRecheckLicensing}
                    disabled={recheckingLicensing}
                    className="sm:w-auto"
                  >
                    {recheckingLicensing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Recheck PRO
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  We'll automatically determine the primary PRO after this track is created.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Album Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Album</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.albumId || 'none'}
                onValueChange={(value) =>
                  handleInputChange('albumId', value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an album (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No album</SelectItem>
                  {albums.map(
                    (album: any) =>
                      album && (
                        <SelectItem key={album.id} value={album.id}>
                          {album.name}
                          {album.releaseDate && (
                            <span className="text-muted-foreground ml-2">
                              ({new Date(album.releaseDate).getFullYear()})
                            </span>
                          )}
                        </SelectItem>
                      )
                  )}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Cover Art Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cover Art</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4">
                <div className="border-muted relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed">
                  {coverPreview ? (
                    <img
                      src={coverPreview}
                      alt="Cover preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="text-muted-foreground h-8 w-8" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('cover', e.target.files?.[0] || null)}
                    className="text-sm"
                  />
                  {formData.cover && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleFileChange('cover', null)}
                    >
                      <X className="mr-1 h-3 w-3" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audio Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Audio File</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileChange('audio', e.target.files?.[0] || null)}
                  className="text-sm"
                />
                {(audioPreview || formData.audio) && (
                  <div className="bg-muted flex items-center justify-between rounded p-2">
                    <div className="flex items-center">
                      <VolumeX className="mr-2 h-4 w-4" />
                      <span className="text-sm">
                        {formData.audio?.name || 'Current audio file'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFileChange('audio', null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Artists Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Artists</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-32">
                <div className="space-y-2">
                  {artists.map(
                    (artist: any) =>
                      artist && (
                        <div key={artist.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`artist-${artist.id}`}
                            checked={formData.artistIds.includes(artist.id)}
                            onCheckedChange={() => handleArtistToggle(artist.id)}
                          />
                          <Label htmlFor={`artist-${artist.id}`} className="text-sm font-normal">
                            {artist.name}
                          </Label>
                        </div>
                      )
                  )}
                </div>
              </ScrollArea>
              {formData.artistIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.artistIds.map((artistId) => {
                    const artist = artists.find((a: any) => a?.id === artistId)
                    return artist ? (
                      <Badge key={artistId} variant="secondary" className="text-xs">
                        {artist.name}
                      </Badge>
                    ) : null
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audio Features - Collapsible */}
          <Card>
            <Collapsible open={isAudioFeaturesOpen} onOpenChange={setIsAudioFeaturesOpen}>
              <CollapsibleTrigger asChild>
                <CardHeader className="hover:bg-muted/50 cursor-pointer transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Audio Features (Optional)</CardTitle>
                    {isAudioFeaturesOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <Label htmlFor="acousticness">Acousticness (0.0-1.0)</Label>
                    <Input
                      id="acousticness"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.audioFeatures.acousticness}
                      onChange={(e) => handleAudioFeatureChange('acousticness', e.target.value)}
                      placeholder="0.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="danceability">Danceability (0.0-1.0)</Label>
                    <Input
                      id="danceability"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.audioFeatures.danceability}
                      onChange={(e) => handleAudioFeatureChange('danceability', e.target.value)}
                      placeholder="0.7"
                    />
                  </div>
                  <div>
                    <Label htmlFor="energy">Energy (0.0-1.0)</Label>
                    <Input
                      id="energy"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.audioFeatures.energy}
                      onChange={(e) => handleAudioFeatureChange('energy', e.target.value)}
                      placeholder="0.8"
                    />
                  </div>
                  <div>
                    <Label htmlFor="valence">Valence (0.0-1.0)</Label>
                    <Input
                      id="valence"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.audioFeatures.valence}
                      onChange={(e) => handleAudioFeatureChange('valence', e.target.value)}
                      placeholder="0.6"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tempo">Tempo (BPM)</Label>
                    <Input
                      id="tempo"
                      type="number"
                      min="0"
                      value={formData.audioFeatures.tempo}
                      onChange={(e) => handleAudioFeatureChange('tempo', e.target.value)}
                      placeholder="120"
                    />
                  </div>
                  <div>
                    <Label htmlFor="loudness">Loudness (dB)</Label>
                    <Input
                      id="loudness"
                      type="number"
                      step="0.1"
                      value={formData.audioFeatures.loudness}
                      onChange={(e) => handleAudioFeatureChange('loudness', e.target.value)}
                      placeholder="-5.0"
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label htmlFor="lyrics">Lyrics</Label>
                    <Textarea
                      id="lyrics"
                      value={formData.audioFeatures.lyrics}
                      onChange={(e) => handleAudioFeatureChange('lyrics', e.target.value)}
                      placeholder="Enter track lyrics..."
                      rows={4}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Update Track' : 'Create Track'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  )
}
